import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { SerializedWorkSummary } from './SerializedWorkSummary';
import { WorkOrderFindingsSummary } from './WorkOrderFindingsSummary';
import { PRIORITY_OPTIONS, normalizePriority } from '../lib/priority';

const _localDatetime = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  } catch (e) {
    return '';
  }
};

const EditWorkOrderDialog = ({ open, onClose, workOrder, onSuccess }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    status: 'not_started',
    priority: '',
    due_date: '',
    assigned_to: '',
    notes: '',
    frequency_days: '',
    frequency_hours: '',
    hours_due_at: '',
    reminder_enabled: false,
    // Completion fields (editable when status === 'completed')
    completed_at_local: '',
    hours_spent: '',
    parts_used: '',
    completion_notes: '',
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (workOrder) {
      let dueDateFormatted = '';
      if (workOrder.due_date) {
        try {
          // Use LOCAL calendar components so a due_date stored as UTC
          // 23:59:59 (which for anyone west of UTC lands on the "next day"
          // in UTC) doesn't walk forward each time the dialog opens.
          const date = new Date(workOrder.due_date);
          const y = date.getFullYear();
          const m = String(date.getMonth() + 1).padStart(2, '0');
          const d = String(date.getDate()).padStart(2, '0');
          dueDateFormatted = `${y}-${m}-${d}`;
        } catch (e) {
          console.error('Error parsing due_date:', e);
        }
      }

      setFormData({
        title: workOrder.title || '',
        description: workOrder.description || '',
        status: workOrder.status || 'not_started',
        priority: normalizePriority(workOrder.priority) || '',
        due_date: dueDateFormatted,
        assigned_to: workOrder.assigned_to || '',
        notes: workOrder.notes || '',
        frequency_days:
          workOrder.frequency_days != null ? String(workOrder.frequency_days) : '',
        frequency_hours:
          workOrder.frequency_hours != null ? String(workOrder.frequency_hours) : '',
        hours_due_at:
          workOrder.hours_due_at != null ? String(workOrder.hours_due_at) : '',
        reminder_enabled: !!workOrder.reminder_enabled,
        completed_at_local: _localDatetime(workOrder.completed_at),
        hours_spent:
          workOrder.hours_spent !== undefined && workOrder.hours_spent !== null
            ? String(workOrder.hours_spent)
            : '',
        parts_used: workOrder.parts_used || '',
        completion_notes: 'completion_notes' in workOrder ? workOrder.completion_notes || '' : workOrder.notes || '',
      });
    }
  }, [workOrder]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Set due date to 11:59 PM on the selected date
      let dueDateISO = null;
      if (formData.due_date) {
        const [year, month, day] = formData.due_date.split('-').map(Number);
        const dueDate = new Date(year, month - 1, day, 23, 59, 59);
        dueDateISO = dueDate.toISOString();
      }

      const submitData = {
        title: formData.title,
        description: formData.description || null,
        status: formData.status,
        priority: formData.priority || null,
        due_date: dueDateISO,
        assigned_to: formData.assigned_to || null,
        notes: formData.notes || null,
        frequency_days: formData.frequency_days
          ? parseInt(formData.frequency_days, 10)
          : null,
        frequency_hours: formData.frequency_hours
          ? parseInt(formData.frequency_hours, 10)
          : null,
        hours_due_at: formData.hours_due_at !== ''
          ? parseFloat(formData.hours_due_at)
          : null,
        reminder_enabled: !!formData.reminder_enabled,
      };

      // Completion fields are only relevant when the WO is (or stays) completed.
      // The backend's `transitioned_to_completed` check makes sure we don't
      // re-deduct inventory or re-spawn recurrence when merely editing a
      // WO that was already completed.
      if (formData.status === 'completed') {
        submitData.completion_notes = formData.completion_notes || null;
        submitData.hours_spent =
          formData.hours_spent === '' ? null : parseFloat(formData.hours_spent);
        submitData.parts_used = formData.parts_used || null;
        submitData.completed_at = formData.completed_at_local === _localDatetime(workOrder.completed_at)
          ? workOrder.completed_at : formData.completed_at_local ? new Date(formData.completed_at_local).toISOString() : null;
      }

      await axios.put(`${API}/work-orders/${workOrder.id}`, submitData);
      toast.success('Work order updated successfully!');
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update work order');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Work Order</DialogTitle>
          <DialogDescription>Update work order details</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <SerializedWorkSummary records={workOrder?.serialized_work} testId="edit-wo-serialized-work" />
          <div className="space-y-2">
            <Label htmlFor="edit-title">Title *</Label>
            <Input
              id="edit-title"
              data-testid="edit-work-order-title-input"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="e.g., Add gas filter to Fill Station 1"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="edit-description">Description</Label>
            <Textarea
              id="edit-description"
              data-testid="edit-work-order-description-textarea"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Detailed description of work to be done"
              rows={3}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="edit-status">Status</Label>
              <select
                id="edit-status"
                data-testid="edit-work-order-status-select"
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                className="flex h-9 w-full items-center justify-between rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm ring-offset-background focus:outline-none focus:ring-1 focus:ring-ring"
              >
                <option value="not_started">Not Started</option>
                <option value="in_progress">In Progress</option>
                <option value="on_hold">On Hold</option>
                {/* "completed" intentionally omitted — use the green Complete
                    button on the work-order card so hours + parts get captured. */}
                {formData.status === 'completed' && <option value="completed">Completed</option>}
              </select>
              {formData.status !== 'completed' && (
                <p className="text-xs text-slate-500">
                  Use the green <strong>Complete</strong> button to close a work order.
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-priority">Priority</Label>
              <select
                id="edit-priority"
                data-testid="edit-work-order-priority-select"
                value={formData.priority}
                onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                className="flex h-9 w-full items-center justify-between rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm ring-offset-background focus:outline-none focus:ring-1 focus:ring-ring"
              >
                <option value="">Select priority</option>
                {PRIORITY_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="edit-due-date">Due Date</Label>
              <Input
                id="edit-due-date"
                data-testid="edit-work-order-due-date-input"
                type="date"
                value={formData.due_date}
                onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-assigned">Assigned To</Label>
              <Input
                id="edit-assigned"
                data-testid="edit-work-order-assigned-input"
                value={formData.assigned_to}
                onChange={(e) => setFormData({ ...formData, assigned_to: e.target.value })}
                placeholder="Technician name"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 items-end">
            <div className="space-y-2">
              <Label htmlFor="edit-frequency">Maintenance Frequency (days)</Label>
              <Input
                id="edit-frequency"
                data-testid="edit-work-order-frequency-input"
                type="number"
                min="1"
                value={formData.frequency_days}
                onChange={(e) => setFormData({ ...formData, frequency_days: e.target.value })}
                placeholder="e.g., 30, 90, 365"
              />
              <p className="text-xs text-slate-500">
                If set, marking this complete will create the next one automatically.
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-reminder" className="block">Enable maintenance reminders</Label>
              <div className="h-9 flex items-center">
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input
                    id="edit-reminder"
                    data-testid="edit-work-order-reminder-toggle"
                    type="checkbox"
                    className="h-4 w-4"
                    checked={!!formData.reminder_enabled}
                    onChange={(e) => setFormData({ ...formData, reminder_enabled: e.target.checked })}
                  />
                  <span className="text-sm text-slate-700">Remind me about this work order</span>
                </label>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="edit-hours-due">Due at Meter Reading (hrs)</Label>
              <Input
                id="edit-hours-due"
                data-testid="edit-work-order-hours-due-input"
                type="number"
                min="0"
                step="0.1"
                value={formData.hours_due_at}
                onChange={(e) => setFormData({ ...formData, hours_due_at: e.target.value })}
                placeholder="e.g., 24000"
              />
              <p className="text-xs text-slate-500">
                Also due when the equipment meter hits this reading — whichever comes first.
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-frequency-hours">Frequency (run hours)</Label>
              <Input
                id="edit-frequency-hours"
                data-testid="edit-work-order-frequency-hours-input"
                type="number"
                min="1"
                value={formData.frequency_hours}
                onChange={(e) => setFormData({ ...formData, frequency_hours: e.target.value })}
                placeholder="e.g., 4000, 8000"
              />
              <p className="text-xs text-slate-500">
                On completion the next WO is due at current meter + this many hours.
              </p>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="edit-notes">Notes</Label>
            <Textarea
              id="edit-notes"
              data-testid="edit-work-order-notes-textarea"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Additional notes or requirements"
              rows={3}
            />
          </div>

          {formData.status === 'completed' && (
            <div className="border-t border-slate-200 pt-4 mt-2 space-y-4">
              <div className="space-y-2">
                <Label htmlFor="edit-wo-completion-notes">Completion notes</Label>
                <Textarea id="edit-wo-completion-notes" data-testid="edit-wo-completion-notes" rows={3}
                  value={formData.completion_notes} onChange={(e) => setFormData({ ...formData, completion_notes: e.target.value })} />
              </div>
              <WorkOrderFindingsSummary findings={workOrder?.findings} machineryId={workOrder?.machinery_id} testId="edit-wo-findings" />
              <div className="text-sm font-medium text-slate-700">
                Completion record
                <span className="ml-2 text-xs text-slate-500 font-normal">
                  (editing these fields does not re-deduct inventory)
                </span>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-completed-at">Completed On</Label>
                  <Input
                    id="edit-completed-at"
                    data-testid="edit-work-order-completed-at-input"
                    type="datetime-local"
                    step="1"
                    value={formData.completed_at_local}
                    onChange={(e) => setFormData({ ...formData, completed_at_local: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-hours-spent">Hours Spent</Label>
                  <Input
                    id="edit-hours-spent"
                    data-testid="edit-work-order-hours-spent-input"
                    type="number"
                    inputMode="decimal"
                    step="0.25"
                    min="0"
                    value={formData.hours_spent}
                    onChange={(e) => setFormData({ ...formData, hours_spent: e.target.value })}
                    placeholder="e.g., 1.5"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-parts-used">Parts Used</Label>
                <Input
                  id="edit-parts-used"
                  data-testid="edit-work-order-parts-used-input"
                  value={formData.parts_used}
                  onChange={(e) => setFormData({ ...formData, parts_used: e.target.value })}
                  placeholder='e.g., "SKF-6205 x2, FILT-01"'
                />
                <p className="text-xs text-slate-500">
                  Freeform text — matches the format captured at completion time.
                </p>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading} data-testid="update-work-order-btn">
              {loading ? 'Updating...' : 'Update Work Order'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EditWorkOrderDialog;
